import { useState } from 'react';
import { Mic, MicOff, Square, Send, Trash2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import { useCharlotteVoice } from '../hooks/useCharlotteVoice';

const CHARLOTTE_URL = import.meta.env.VITE_CHARLOTTE_URL || 'https://charlotte.netrunsystems.com:8080';

const KOG_INSTRUCTIONS = `You are KOG, an AI sales assistant for Netrun Systems.
You have access to the sales pipeline, contact intelligence, and company research
via the kog_sales RAG collection. Help the user manage deals, draft outreach emails,
analyze contact engagement, and provide pipeline insights. Be concise and action-oriented.
When asked about a contact or deal, query the knowledge base for relevant intelligence.`;

export default function VoicePage() {
  const [textInput, setTextInput] = useState('');

  const voice = useCharlotteVoice({
    apiUrl: CHARLOTTE_URL,
    ragCollection: 'kog_sales',
    instructions: KOG_INSTRUCTIONS,
    voice: 'Kore',
  });

  const handleSendText = () => {
    if (textInput.trim()) {
      voice.sendText(textInput);
      setTextInput('');
    }
  };

  return (
    <div className="page-enter flex flex-col h-[calc(100vh-40px)]">
      <Header title="Charlotte Voice" subtitle="Gemini-powered sales assistant" />

      <div className="flex-1 flex flex-col md:flex-row gap-4 md:gap-6 p-4 md:p-8 min-h-0">
        {/* Transcript panel */}
        <div className="flex-1 flex flex-col min-h-0">
          <Panel title="Conversation" className="flex-1 flex flex-col min-h-0" noPadding>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {voice.transcripts.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Mic size={48} style={{ color: 'var(--border-subtle)', margin: '0 auto 16px' }} />
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      Click the microphone to start a voice conversation
                    </p>
                    <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                      Or type a message below
                    </p>
                  </div>
                </div>
              )}

              {voice.transcripts.map((t) => (
                <div
                  key={t.id}
                  className="rounded-lg p-4"
                  style={{
                    background: t.source === 'user' ? 'var(--bg-input)' : 'var(--bg-primary)',
                    borderLeft: `3px solid ${t.source === 'user' ? 'var(--netrun-green)' : 'var(--info)'}`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-display"
                      style={{ color: t.source === 'user' ? 'var(--netrun-green)' : 'var(--info)' }}>
                      {t.source === 'user' ? 'You' : 'KOG'}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {new Date(t.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{t.text}</p>
                </div>
              ))}
            </div>

            {/* Text input */}
            <div className="p-4" style={{ borderTop: '1px solid var(--border-primary)' }}>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendText()}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-3 rounded-full text-sm outline-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                />
                <button
                  onClick={handleSendText}
                  className="w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-all"
                  style={{
                    background: 'var(--netrun-green)',
                    color: 'var(--netrun-black)',
                    border: 'none',
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </Panel>
        </div>

        {/* Controls sidebar */}
        <div className="w-full md:w-[280px] space-y-4">
          {/* Voice control */}
          <Panel title="Voice Control">
            <div className="flex flex-col items-center gap-4">
              {/* Main mic button with pulse ring */}
              <div className="relative flex items-center justify-center">
                {voice.isActive && (
                  <span
                    className="absolute w-28 h-28 rounded-full"
                    style={{
                      border: '2px solid var(--netrun-green)',
                      opacity: 0.4,
                      animation: 'mic-pulse-ring 2s ease-out infinite',
                    }}
                  />
                )}
                <button
                  onClick={voice.isActive ? voice.stop : voice.start}
                  className="w-24 h-24 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 relative z-10"
                  style={{
                    background: voice.isActive ? 'var(--netrun-green)' : 'var(--bg-input)',
                    color: voice.isActive ? 'var(--netrun-black)' : 'var(--text-secondary)',
                    border: `2px solid ${voice.isActive ? 'var(--netrun-green)' : 'var(--border-subtle)'}`,
                    boxShadow: voice.isActive ? '0 0 30px rgba(144,185,171,0.3)' : 'none',
                    animation: voice.isSpeaking ? 'pulse-sage 1.5s infinite' : 'none',
                  }}
                >
                  {voice.isActive ? <MicOff size={32} /> : <Mic size={32} />}
                </button>
              </div>

              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {voice.isActive
                  ? voice.isSpeaking ? 'KOG is speaking...' : voice.isConnected ? 'Listening...' : 'Connecting...'
                  : 'Click to start'}
              </p>

              {/* Secondary controls */}
              {voice.isActive && (
                <div className="flex gap-3">
                  <button
                    onClick={voice.toggleMute}
                    className="px-4 min-h-[48px] rounded-lg text-xs cursor-pointer transition-all"
                    style={{
                      background: voice.isMuted ? 'var(--error)' : 'var(--bg-input)',
                      color: voice.isMuted ? 'white' : 'var(--text-secondary)',
                      border: `1px solid ${voice.isMuted ? 'var(--error)' : 'var(--border-subtle)'}`,
                    }}
                  >
                    {voice.isMuted ? 'Unmute' : 'Mute'}
                  </button>
                  <button
                    onClick={voice.cancel}
                    className="px-4 min-h-[48px] min-w-[48px] rounded-lg text-xs cursor-pointer transition-all flex items-center justify-center"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}
                  >
                    <Square size={12} />
                  </button>
                </div>
              )}
            </div>
          </Panel>

          {/* Stats */}
          <Panel title="Session Stats">
            <div className="space-y-3 text-xs">
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-muted)' }}>Audio sent</span>
                <span style={{ color: 'var(--text-secondary)' }}>{voice.stats.audioChunksSent}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-muted)' }}>Audio received</span>
                <span style={{ color: 'var(--text-secondary)' }}>{voice.stats.audioChunksReceived}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-muted)' }}>Responses</span>
                <span style={{ color: 'var(--text-secondary)' }}>{voice.stats.responsesCount}</span>
              </div>
              {voice.engagement && (
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-muted)' }}>Engagement</span>
                  <span style={{ color: 'var(--netrun-green)' }}>{voice.engagement.signal}</span>
                </div>
              )}
            </div>
          </Panel>

          {/* Clear */}
          <button
            onClick={voice.clearTranscripts}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-xs cursor-pointer transition-all"
            style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border-primary)' }}
          >
            <Trash2 size={14} />
            Clear Conversation
          </button>
        </div>
      </div>
    </div>
  );
}
